import React, {forwardRef, Ref, useState} from 'react';
import {NewsletterProps} from '../../../../types';
import {
    NewsletterSectionWrapper,
    NewsletterSectionInner,
    NewsletterSectionBackground
} from './NewsletterSection.styles';
import {Eyebrow} from '../../eyebrow';
import {VerticalSpacer} from '../../spacer';
import {Typography} from '../../typography';
import {Select, SelectMenuItem, TextField} from '../../inputs';
import {Hyperlink} from '../../links';
import {Button} from '../../buttons';
import {withNewsletterLogic} from '../../../wraps/newsletter/Newsletter.wrap';
import {getLocale} from '@epic-core/common/dist';
import {GridContainer} from '../../grid';
import Grid from '@material-ui/core/Grid';
import {useBreakpointDown, useIsTwinmotionTheme} from 'epic-ue-theme';
import {CheckIcon} from '../../icons';
import {useSetSectionNewsletterPresence} from './newsletterSection.hooks';
import {useMarketoGlobalOptIn} from 'epic-ue-newsletter';
import {useUnrealUIContext} from '../../../../shared/context';

export type NewsletterVariantOptions = 'split' | 'small' | 'small-split';
export interface NewsletterSectionProps extends NewsletterProps {
    variant?: NewsletterVariantOptions;
    ref?: Ref<any>;
    accentColorDark?: boolean;
}

const NewsletterSectionWithLogic = withNewsletterLogic<NewsletterSectionProps>(
    ({
        accentColor = 'sky',
        accentColorDark = true,
        eyebrow,
        title,
        emailInputValue,
        emailInputError,
        emailInputHelperText,
        emailInputPlaceholder,
        emailInputDisable,
        industryInputError,
        industryInputHelperText,
        industryPlaceholder,
        staticIndustry,
        submitButtonName,
        industrySelected,
        privacyPolicyHref,
        privacyPolicyDescription,
        privacyPolicyLinkName,
        industries = [],
        onChange,
        onSubmit,
        onBlur,
        variant = undefined,
        ref,
        disable,
        disableSubmitButton,
        subscribed,
        successTitle,
        successMessage
    }: NewsletterSectionProps): JSX.Element => {
        const selectMenuItems: JSX.Element[] = [];
        const isTwinmotion = useIsTwinmotionTheme();
        const {staticIndustry: uiContextStaticIndustry} = useUnrealUIContext();
        const finalStaticIndustry = uiContextStaticIndustry || staticIndustry;
        const [selectedIndustry, setSelectedIndustry] = useState(industrySelected);
        const [email, setEmail] = useState<string | undefined>(emailInputValue);
        const className = variant ? variant.split('-').join(' ') : '';
        const isSm = useBreakpointDown('sm');
        const optedIn = useMarketoGlobalOptIn();
        const alreadySubscribed = optedIn || subscribed;

        const small = className.indexOf('small') > -1;
        const split = className.indexOf('split') > -1;

        const wrapperProps = {
            className: `ue-newsletter-section ${className} ${isTwinmotion ? 'twinmotion' : ''}`
        };

        const innerProps = {
            className
        };

        let placeholder = industryPlaceholder || 'your industry';

        const getLocalizedIndustryName = (id: string) => {
            for (const industry of industries) {
                if (industry.id === id) {
                    return industry.name;
                }
            }
            return id;
        };

        const getIndustryId = (name: string) => {
            for (const industry of industries) {
                if (industry.name === name) {
                    return industry.id;
                }
            }
            return name;
        };

        for (const industry of industries) {
            if (industry.id === 'placeholder') {
                placeholder = industry.name || placeholder;
            } else {
                selectMenuItems.push(
                    <SelectMenuItem id={industry.id} key={industry.id} value={industry.id}>
                        {industry.name}
                    </SelectMenuItem>
                );
            }
        }

        const handleChange = (event: React.ChangeEvent<{name?: string; value: unknown}>) => {
            switch (event.target.name) {
                case 'email':
                    if (onChange && typeof onChange === 'function') {
                        onChange('email', event.target.value as string);
                    }
                    setEmail(event.target.value as string);
                    break;
                case 'industry':
                    if (onChange && typeof onChange === 'function') {
                        onChange('industry', event.target.value as string);
                    }
                    setSelectedIndustry(getLocalizedIndustryName(event.target.value as string));
                    break;
                default:
                    break;
            }
        };

        const handleBlur = (event: React.ChangeEvent<{name?: string; value: unknown}>) => {
            switch (event.target.name) {
                case 'email':
                    if (onBlur && typeof onBlur === 'function') {
                        onBlur('email', event.target.value as string);
                    }
                    break;
                case 'industry':
                    if (onBlur && typeof onBlur === 'function') {
                        onBlur('industry', event.target.value as string);
                    }
                    break;
                default:
                    break;
            }
        };

        return (
            <NewsletterSectionWrapper ref={ref} {...wrapperProps} emailInputError={emailInputError}>
                {split && (
                    <NewsletterSectionBackground
                        className={`news-section-bg ${isTwinmotion ? 'twinmotion' : ''}`}
                    />
                )}
                <GridContainer className="newsletter-grid">
                    <NewsletterSectionInner {...innerProps}>
                        <Grid
                            container
                            direction={small && !isSm ? 'row' : 'column'}
                            justifyContent={small && !isSm ? 'space-evenly' : 'center'}
                            alignItems={'center'}>
                            {!small && (
                                <Grid item md={6} sm={11} xs={11}>
                                    <Eyebrow {...eyebrow} />
                                    <VerticalSpacer variant="custom" spacing={3} />
                                </Grid>
                            )}
                            {alreadySubscribed && (
                                <Grid item md={small ? 1 : 8} sm={11} xs={11}>
                                    {(!small || isSm) && (
                                        <VerticalSpacer
                                            variant="custom"
                                            spacing={5}
                                            downSmSpacing={1}
                                        />
                                    )}
                                    <CheckIcon color={accentColor} />
                                    {(!small || isSm) && (
                                        <VerticalSpacer variant="custom" spacing={4} />
                                    )}
                                </Grid>
                            )}
                            <Grid item md={small ? 4 : 8} sm={11} xs={11}>
                                <Typography
                                    className={`${small ? 'newsletter-section-description-small' : ''}`}
                                    variant={small || isSm ? 'h3' : 'h2'}>
                                    {alreadySubscribed ? (
                                        successTitle
                                    ) : (
                                        <>
                                            {title}
                                            &nbsp;
                                            {!finalStaticIndustry ? (
                                                <Select
                                                    className="newsletter-section-select"
                                                    onChange={handleChange}
                                                    name="industry"
                                                    accentColor={accentColor}
                                                    value={selectedIndustry}
                                                    error={industryInputError}
                                                    helperText={industryInputHelperText}
                                                    disabled={disable}
                                                    displayEmpty={true}
                                                    renderValue={(value: any) =>
                                                        value ? value : placeholder
                                                    }>
                                                    {selectMenuItems}
                                                </Select>
                                            ) : (
                                                ''
                                            )}
                                        </>
                                    )}
                                </Typography>
                                {alreadySubscribed
                                    ? (!small || isSm) && (
                                          <VerticalSpacer
                                              variant="custom"
                                              spacing={2}
                                              downSmSpacing={1}
                                          />
                                      )
                                    : (!small || isSm) && (
                                          <VerticalSpacer variant="custom" spacing={4} />
                                      )}
                            </Grid>
                            {!alreadySubscribed && (
                                <Grid
                                    container
                                    item
                                    lg={7}
                                    md={small ? 6 : 6}
                                    sm={8}
                                    xs={8}
                                    direction={'row'}
                                    justifyContent={'center'}
                                    className="newsletter-section-input-grid-container">
                                    <Grid item md={5} sm={12} xs={12}>
                                        <TextField
                                            className="newsletter-section-input"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            name="email"
                                            value={emailInputValue ? emailInputValue : email}
                                            error={emailInputError}
                                            helperText={emailInputHelperText}
                                            placeholder={emailInputPlaceholder}
                                            disabled={emailInputDisable || disable}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        className={'button-grid-item'}>
                                        <Button
                                            onClick={() => {
                                                if (onSubmit && typeof onSubmit === 'function') {
                                                    onSubmit(
                                                        email,
                                                        selectedIndustry &&
                                                            getIndustryId(selectedIndustry)
                                                    );
                                                }
                                            }}
                                            aria-label="Subscribe"
                                            color={accentColor}
                                            disabled={disable || disableSubmitButton}>
                                            {submitButtonName || 'Subscribe'}
                                        </Button>
                                    </Grid>
                                    {small && <Grid
                                        item
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        className={'privacypolicy-grid-item'}>
                                        <Hyperlink
                                            target="_blank"
                                            accentColor={accentColor}
                                            accentColorDark={accentColorDark}
                                            href={
                                                privacyPolicyHref ||
                                                `https://www.epicgames.com/${getLocale()}/privacypolicy`
                                            }>
                                            {privacyPolicyLinkName || 'Privacy policy'}
                                        </Hyperlink>
                                    </Grid>}
                                </Grid>
                            )}
                            {!alreadySubscribed && (!small || isSm) && (
                                <VerticalSpacer variant="custom" spacing={3} />
                            )}
                            {alreadySubscribed ? (
                                <Grid item md={6} sm={11} xs={11}>
                                    <Typography variant="p" caption>
                                        {successMessage}
                                    </Typography>
                                </Grid>
                            ) : (
                                !small && (
                                    <Grid item md={small ? 1 : 6} sm={11} xs={11}>
                                        <Typography variant="p" caption>
                                            {privacyPolicyDescription}
                                            &nbsp;
                                            <Hyperlink
                                                target="_blank"
                                                accentColor={accentColor}
                                                accentColorDark={accentColorDark}
                                                href={
                                                    privacyPolicyHref ||
                                                    `https://www.epicgames.com/${getLocale()}/privacypolicy`
                                                }>
                                                {privacyPolicyLinkName || 'Privacy policy'}
                                            </Hyperlink>
                                        </Typography>
                                    </Grid>
                                    
                                )
                            )}
                        </Grid>
                    </NewsletterSectionInner>
                </GridContainer>
            </NewsletterSectionWrapper>
        );
    }
);

export const NewsletterSection = forwardRef<HTMLDivElement, NewsletterSectionProps>(
    (props, ref) => {
        useSetSectionNewsletterPresence();
        return <NewsletterSectionWithLogic {...props} ref={ref} />;
    }
);

NewsletterSection.displayName = 'newsletterSection';
